import { useNavigate, Link } from "react-router-dom";
import { useResetPassword } from "../../queries/auth";
import { ResetPasswordDetailModel } from "../../types/auth";
import { toast } from "react-toastify";
import AuthLayout from "../../components/component/AuthLayout";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

interface ChangePasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your password"),
});

export default function ChangePasswordPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const jwt = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { mutate: resetPassword, isPending } = useResetPassword();

  const initialValues: ChangePasswordFormValues = {
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = (
    values: ChangePasswordFormValues,
    { setSubmitting, resetForm }: any,
  ) => {
    if (!jwt) {
      toast("Invalid reset token", {
        type: "error",
        autoClose: 2000,
      });
      setSubmitting(false);
      return;
    }

    const passwordDto: ResetPasswordDetailModel = {
      newPassword: values.newPassword,
      jwt,
    };

    resetPassword(passwordDto, {
      onSuccess: () => {
        resetForm();
        toast("Password changed successfully", {
          type: "success",
          autoClose: 2000,
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      },
      onError: (error: any) => {
        toast(
          error?.response?.data?.errorMessage || "Please try again, Timeout",
          {
            type: "error",
            autoClose: 2000,
          },
        );
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  return (
    <AuthLayout>
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">
          Change Password
        </h1>
        <p className="text-gray-500 mb-8">
          Create a new secure password for your account
        </p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-6">
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  New Password
                </label>
                <Field
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  className="w-full px-4 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your new password"
                />
                <ErrorMessage
                  name="newPassword"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Confirm Password
                </label>
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="w-full px-4 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Confirm your new password"
                />
                <ErrorMessage
                  name="confirmPassword"
                  component="p"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>Password requirements:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>At least 8 characters long</li>
                  <li>Mix of uppercase and lowercase letters recommended</li>
                  <li>
                    Include numbers and special characters for better security
                  </li>
                </ul>
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white font-medium py-3 px-4 rounded-md hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || isPending}
              >
                {isSubmitting || isPending
                  ? "Changing Password..."
                  : "Change Password"}
              </button>

              <Link
                to="/login"
                className="flex items-center justify-center text-sm text-blue-500 hover:underline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
                Back to Login
              </Link>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
}
